// The three date windows, in a popover off the toolbar.
//
// A popover rather than three pairs of inputs on the toolbar: six date fields
// and two checkboxes across the top of the board would push the counts and the
// search off the row on a laptop, and the dates are asked for occasionally while
// the counts are read constantly.
//
// All of the logic is in utils/tasks.ts (EMPTY_DATE_FILTER, dateFilterParams,
// activeDateFilterCount) so it can be tested without a browser. This file is
// controls only.
import { CalendarRange, X } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Popover, PopoverContent, PopoverTrigger } from '../../lib/shadcn/popover'
import { cn } from '../../lib/shadcn/utils'
import {
  EMPTY_DATE_FILTER,
  activeDateFilterCount,
  dateFilterIsEmpty,
  type TaskDateFilter,
} from '../../utils/tasks'

function Window({
  label,
  hint,
  from,
  to,
  onFrom,
  onTo,
  undated,
  onUndated,
  undatedLabel,
  undatedCount,
}: {
  label: string
  hint: string
  from: string
  to: string
  onFrom: (v: string) => void
  onTo: (v: string) => void
  undated?: boolean
  onUndated?: (v: boolean) => void
  undatedLabel?: string
  undatedCount?: number
}) {
  const active = Boolean(from || to)
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-foreground">
          {label}
        </span>
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          type="date"
          value={from}
          onChange={(e) => onFrom(e.target.value)}
          className="h-8 text-xs"
          aria-label={`${label} from`}
        />
        <span className="text-xs text-muted-foreground">to</span>
        <Input
          type="date"
          value={to}
          onChange={(e) => onTo(e.target.value)}
          className="h-8 text-xs"
          aria-label={`${label} to`}
        />
      </div>
      {onUndated && (
        // Shown greyed rather than hidden when the window is empty, so the
        // toggle is where you expect it the moment you pick a date, and so its
        // state is visible before it starts mattering.
        <label
          className={cn(
            'flex items-center gap-2 text-[11px]',
            active ? 'text-foreground' : 'text-muted-foreground/60',
          )}
        >
          <input
            type="checkbox"
            checked={undated ?? true}
            disabled={!active}
            onChange={(e) => onUndated(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border"
          />
          {undatedLabel}
          {typeof undatedCount === 'number' && undatedCount > 0 && (
            <span className="tabular-nums opacity-70">({undatedCount})</span>
          )}
        </label>
      )}
    </div>
  )
}

export function TaskDateFilters({
  value,
  onChange,
  noStartCount,
  noTargetCount,
  backlogHeldCount,
}: {
  value: TaskDateFilter
  onChange: (next: TaskDateFilter) => void
  /** How many tasks currently on the board carry no date in that column. */
  noStartCount?: number
  noTargetCount?: number
  // How many rows are on the board only because the backlog box is ticked.
  // Computed by backlogHeldCount in utils/tasks.ts against the same rule the
  // server applies, so the number beside the box is the number it is holding.
  backlogHeldCount?: number
}) {
  const count = activeDateFilterCount(value)
  const set = (patch: Partial<TaskDateFilter>) => onChange({ ...value, ...patch })

  // The backlog box only does anything once a Start or Target window is set.
  // Created is not in this test, because the exemption does not cover it.
  const scheduleActive = Boolean(
    value.startFrom || value.startTo || value.targetFrom || value.targetTo,
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-7 gap-1.5 px-2.5 text-xs',
            count > 0 && 'border-[hsl(var(--brand-soft-foreground))] text-[hsl(var(--brand-soft-foreground))]',
          )}
        >
          <CalendarRange className="h-3.5 w-3.5" />
          Dates
          {count > 0 && <span className="tabular-nums">({count})</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3">
        <Window
          label="Created"
          hint="when it landed on us"
          from={value.createdFrom}
          to={value.createdTo}
          onFrom={(v) => set({ createdFrom: v })}
          onTo={(v) => set({ createdTo: v })}
        />
        <Window
          label="Start"
          hint="when we meant to begin"
          from={value.startFrom}
          to={value.startTo}
          onFrom={(v) => set({ startFrom: v })}
          onTo={(v) => set({ startTo: v })}
          undated={value.startIncludeUndated}
          onUndated={(v) => set({ startIncludeUndated: v })}
          undatedLabel="Keep tasks with no start date"
          undatedCount={noStartCount}
        />
        <Window
          label="Target"
          hint="when we promised it"
          from={value.targetFrom}
          to={value.targetTo}
          onFrom={(v) => set({ targetFrom: v })}
          onTo={(v) => set({ targetTo: v })}
          undated={value.targetIncludeUndated}
          onUndated={(v) => set({ targetIncludeUndated: v })}
          undatedLabel="Keep tasks with no target date"
          undatedCount={noTargetCount}
        />

        <p className="border-t border-border pt-2 text-[11px] text-muted-foreground">
          Windows combine: a task has to fall inside every one you set. Both ends
          are optional, so a single date reads as "from then on" or "up to then".
        </p>

        {/* A box rather than a rule nobody can see. Ticked by default, and
            untickable, because "show me only what actually falls in the window"
            is a real question and a permanent exemption would refuse to answer
            it. 16 September 2026.

            Greyed and disabled until a scheduling window is set, matching the
            two undated toggles above: the control is where you expect it before
            it starts mattering, and its state is visible either way. */}
        <label
          className={cn(
            'flex items-start gap-2 border-t border-border pt-2 text-[11px]',
            scheduleActive ? 'text-foreground' : 'text-muted-foreground/60',
          )}
        >
          <input
            type="checkbox"
            checked={value.backlogIgnoresDates}
            disabled={!scheduleActive}
            onChange={(e) => set({ backlogIgnoresDates: e.target.checked })}
            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-border"
          />
          <span>
            <span className="font-medium">Keep Not Started, On-Hold and Blocked</span>
            {typeof backlogHeldCount === 'number' && backlogHeldCount > 0 && (
              <span className="ml-1 tabular-nums opacity-70">({backlogHeldCount})</span>
            )}
            <span className="block opacity-80">
              Those three groups stay on the board whatever the Start and Target
              windows say. Untick it to see only what actually falls in the window.
              Created is never exempt either way.
            </span>
          </span>
        </label>

        <p className="text-[11px] text-muted-foreground">
          A task and its subtasks arrive together. If any one of them falls in the
          window, the parent and all of its subtasks come with it.
        </p>

        {!dateFilterIsEmpty(value) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-full gap-1.5 text-xs"
            onClick={() => onChange(EMPTY_DATE_FILTER)}
          >
            <X className="h-3.5 w-3.5" />
            Clear all dates
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
